"use client";

/**
 * Error Page (500 / Runtime Errors)
 * 
 * แสดงเมื่อเกิดข้อผิดพลาดระหว่างการประมวลผล
 * พร้อมข้อความอธิบายและแนวทางแก้ไข
 * 
 * TOR Reference: Error Handling - ข้อความอธิบายชัดเจนเมื่อเกิดข้อผิดพลาด
 */

import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Home, RefreshCw, AlertTriangle, HelpCircle, Bug } from "lucide-react";

interface ErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
    useEffect(() => {
        // Log error to console (in production, send to error tracking service)
        console.error("Application Error:", error);
    }, [error]);

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-red-50/50 to-background">
            {/* Header */}
            <header className="border-b bg-white/80 backdrop-blur">
                <div className="container mx-auto flex h-16 items-center px-4">
                    <Link href="/" className="flex items-center gap-3">
                        <Image
                            src="/logo_cus.png"
                            alt="กรมศุลกากร"
                            width={40}
                            height={40}
                            className="h-10 w-10 object-contain"
                        />
                        <div className="hidden sm:block">
                            <p className="text-sm font-semibold text-primary">กรมศุลกากร</p>
                            <p className="text-xs text-muted-foreground">Thai Customs Department</p>
                        </div>
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-lg w-full text-center">
                    {/* Error Icon */}
                    <div className="mb-8">
                        <div className="inline-flex h-24 w-24 items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
                            <AlertTriangle className="h-12 w-12" />
                        </div>
                    </div>

                    {/* Error Message */}
                    <div className="mb-8">
                        <h1 className="text-2xl font-semibold text-foreground mb-3">
                            เกิดข้อผิดพลาดในระบบ
                        </h1>
                        <p className="text-muted-foreground leading-relaxed">
                            ขออภัย ระบบไม่สามารถดำเนินการตามที่ร้องขอได้ในขณะนี้ 
                            กรุณาลองใหม่อีกครั้ง หากยังพบปัญหา กรุณาติดต่อเจ้าหน้าที่
                        </p>
                    </div>

                    {/* Solutions */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                        <div className="flex items-start gap-3">
                            <HelpCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-medium text-amber-900 mb-2">แนวทางแก้ไข:</p>
                                <ul className="text-sm text-amber-800 space-y-1">
                                    <li>• กด &quot;ลองใหม่อีกครั้ง&quot; เพื่อโหลดหน้าใหม่</li>
                                    <li>• ล้างข้อมูลแคช (Cache) ของเบราว์เซอร์</li>
                                    <li>• ลองเปลี่ยนเบราว์เซอร์ หรืออุปกรณ์อื่น</li>
                                    <li>• หากยังพบปัญหา ติดต่อ 1164</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Error Details (for debugging in development) */}
                    {process.env.NODE_ENV === "development" && error.message && (
                        <div className="bg-gray-100 border rounded-lg p-4 mb-6 text-left">
                            <div className="flex items-start gap-3">
                                <Bug className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                                <div className="overflow-hidden">
                                    <p className="font-medium text-gray-900 mb-1 text-sm">
                                        รายละเอียดข้อผิดพลาด (Dev Mode):
                                    </p>
                                    <p className="text-xs text-gray-600 font-mono break-all">
                                        {error.message}
                                    </p>
                                    {error.digest && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Error ID: {error.digest}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button size="lg" onClick={reset}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            ลองใหม่อีกครั้ง
                        </Button>
                        <Button variant="outline" size="lg" asChild>
                            <Link href="/">
                                <Home className="mr-2 h-4 w-4" />
                                กลับหน้าหลัก
                            </Link>
                        </Button>
                    </div>

                    {/* Help Link */}
                    <p className="mt-8 text-sm text-muted-foreground">
                        หากต้องการความช่วยเหลือ{" "}
                        <a href="tel:1164" className="text-primary hover:underline font-medium">
                            โทร 1164
                        </a>{" "}
                        หรือ{" "}
                        <a href="mailto:complaint@customs.go.th" className="text-primary hover:underline font-medium">
                            complaint@customs.go.th
                        </a>
                    </p>
                </div>
            </main>
        </div>
    );
}
